import React, { useMemo, useState, useEffect } from 'react';
import classNames from 'classnames';
import { Segment, Radio } from 'tea-component';
import { useIntl } from 'umi';
import { LANGUAGE_EN } from '@home/_config/config';
import { getName } from '@home/utils/common';
import {
  IRealtimeOverviewTrendProps,
  ISegmentOption,
  IRadioOption,
} from './type';
import './index.scss';
import { dynamicString } from '@home/utils/dynamicString';
import RealtimeUserService from '../../realtimeUsers/component/realtimeUserService';

const RealtimeOverviewTrend: React.FC<IRealtimeOverviewTrendProps> = (props) => {
  const {
    className,
    style,
    segmentConfig,
    radioConfig,
    children,
    data,
    ...restProps
  } = props;

  const { locale } = useIntl();
  const isEn = locale === LANGUAGE_EN;
  const { options: segmentOptions = [], dataKey: segmentDataKey = '', defaultValue: segmentDefaultValue } = segmentConfig || {};
  const { options: radioOptions = [], dataKey: radioDataKey = '', defaultValue: radioDefaultValue } = radioConfig || {};

  // 初始化 Segment 值
  const [segmentValue, setSegmentValue] = useState<string>(() => {
    if (segmentDataKey && data?.[segmentDataKey]) {
      return data[segmentDataKey];
    }
    return segmentDefaultValue || segmentOptions[0]?.value || '';
  });

  // 初始化 Radio 值
  const [radioValue, setRadioValue] = useState<string>(() => {
    if (radioDataKey && data?.[radioDataKey]) {
      return data[radioDataKey];
    }
    return radioDefaultValue || radioOptions[0]?.value || '';
  });

  // 根据当前选中的 segmentValue 动态获取对应的 config
  const { currentSegment, config } = useMemo(() => {
    const currentSegment = segmentOptions.find((item: ISegmentOption) => item.value === segmentValue) || {};
    const currentRadio = radioOptions?.find((item: IRadioOption) => item.value === radioValue) || {};
    const config = radioValue === 'all' ? currentSegment?.allApiConfig : currentSegment?.diminsionApiConfig || {};
    const extraParams = {
      ...(config?.extraParams || {}),
      ...(currentRadio?.extraParams || {})
    };
    return {
      currentSegment,
      config: {
        ...config,
        extraParams
      }
    };
  }, [segmentOptions, radioValue, segmentValue]);

  // 当外部 data 变化时同步内部状态
  useEffect(() => {
    if (segmentDataKey && data?.[segmentDataKey] !== undefined) {
      setSegmentValue(data[segmentDataKey]);
    }
  }, [data, segmentDataKey]);

  useEffect(() => {
    if (radioDataKey && data?.[radioDataKey] !== undefined) {
      setRadioValue(data[radioDataKey]);
    }
  }, [data, radioDataKey]);

  // 生成 Segment 选项
  const segmentOptionsList = useMemo(() => {
    return segmentOptions.map((item: ISegmentOption) => {
      const { value, nameEn, nameCn, tooltipEn, tooltipCn, disabled = false } = item;
      const text = getName(isEn, dynamicString(nameEn, data), dynamicString(nameCn, data));
      return {
        text,
        value,
        disabled,
        tooltip: getName(isEn, dynamicString(tooltipEn || '', data), dynamicString(tooltipCn || '', data)),
      };
    });
  }, [segmentOptions, data, isEn]);

  // 生成 Radio 选项
  const radioOptionsList = useMemo(() => {
    return radioOptions.map((item: IRadioOption) => {
      const { value, nameEn, nameCn, disabled = false } = item;
      const name = getName(isEn, dynamicString(nameEn, data), dynamicString(nameCn, data));
      return {
        name,
        value,
        disabled,
      };
    });
  }, [radioOptions, data, isEn]);

  // 处理 Segment 变化
  const handleSegmentChange = (value: string): void => {
    setSegmentValue(value);
  };

  // 处理 Radio 变化
  const handleRadioChange = (value: string): void => {
    setRadioValue(value);
  };

  return (
    <div
      className={classNames('dbd-realtime-overview-trend-container', className)}
      style={style}
    >
      <div className="dbd-realtime-overview-trend-controls">
        {segmentConfig && segmentOptions.length > 0 && (
          <Segment
            className="dbd-realtime-overview-trend-segment"
            value={segmentValue}
            onChange={handleSegmentChange}
            options={segmentOptionsList}
          />
        )}
        {radioConfig && radioOptions.length > 0 && (
          <Radio.Group
            className="dbd-realtime-overview-trend-radio"
            value={radioValue}
            onChange={handleRadioChange}
          >
            {radioOptionsList.map((item) => (
              <Radio key={item.value} name={item.value} disabled={item.disabled}>
                {item.name}
              </Radio>
            ))}
          </Radio.Group>
        )}
      </div>
      <div className='dbd-realtime-overview-trend-content' key={JSON.stringify(config)}>
        <RealtimeUserService
          {...props}
          data={
            {
              ...(data || {}),
              dataBy: radioValue
            }
          }
          config={config}
          currentSegment={currentSegment}
          segmentValue={segmentValue}
          radioValue={radioValue}
        />
        {/* {React.Children.map(children, (child: any) => React.cloneElement(
          child,
          {
            ...restProps,
            data,
            config,
            segmentValue,
            radioValue,
            // state: {}
          }
        ))} */}
      </div>
    </div>
  );
};

export default RealtimeOverviewTrend;
